import { useEffect, useRef, useState } from "react";

import {
  IdentitySignInCancelledError,
  type IdentityCredential,
  type IdentityClient,
} from "./identity.js";
import type { AuthClientConfig } from "./types.js";

interface SignupProps {
  readonly config: AuthClientConfig;
  readonly identity: IdentityClient | undefined;
  readonly authenticationError?: string | undefined;
  readonly onAuthenticated: (credential: IdentityCredential) => void;
}

const PROMISES = [
  {
    number: "01",
    title: "Bring in what you hold",
    body: "Connect a read-only source, upload a file, or type positions in. Read-only always — Polyfuture never places an order.",
  },
  {
    number: "02",
    title: "Explore what could change",
    body: "What-ifs are built from your actual holdings, each one a chain of steps you can read, argue with, and see the evidence for — on both sides.",
  },
  {
    number: "03",
    title: "Stay informed",
    body: "Each what-if names an observable measure and a threshold. When one crosses, you hear about it with the reason attached.",
  },
] as const;

/**
 * Unavailable providers are rendered as stated facts rather than controls that cannot work. The
 * product uses the same pattern for unqualified position sources.
 */
const UNAVAILABLE_PROVIDERS = [
  { label: "Continue with Microsoft", reason: "No Microsoft identity provider is configured." },
  { label: "Use your company SSO", reason: "SAML and Okta federation are not configured." },
  {
    label: "Continue with an email and password",
    reason: "Password sign-in is deliberately disabled.",
  },
] as const;

const SIGN_IN_FEEDBACK_TIMEOUT_MS = 30_000;

export function Signup({ config, identity, authenticationError, onAuthenticated }: SignupProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const signInAttempt = useRef(0);
  const signInFeedbackTimeout = useRef<number | undefined>(undefined);
  const googleAvailable = config.providers.google && identity !== undefined;

  useEffect(
    () => () => {
      signInAttempt.current += 1;
      window.clearTimeout(signInFeedbackTimeout.current);
    },
    [],
  );

  useEffect(() => {
    if (identity === undefined) return;
    let active = true;
    void identity
      .completeGoogleSignIn()
      .then((credential) => {
        if (active && credential !== undefined) onAuthenticated(credential);
      })
      .catch(() => {
        if (active) {
          setBusy(false);
          setError("Google returned to Polyfuture, but sign-in did not complete. Try again.");
        }
      });
    return () => {
      active = false;
    };
  }, [identity, onAuthenticated]);

  async function continueWithGoogle() {
    if (identity === undefined) return;
    const attempt = signInAttempt.current + 1;
    signInAttempt.current = attempt;
    window.clearTimeout(signInFeedbackTimeout.current);
    setBusy(true);
    setError(undefined);
    signInFeedbackTimeout.current = window.setTimeout(() => {
      if (signInAttempt.current !== attempt) return;
      setBusy(false);
      setError("Google sign-in is taking longer than expected. Try again.");
    }, SIGN_IN_FEEDBACK_TIMEOUT_MS);
    try {
      const issued = await identity.signInWithGoogle();
      if (signInAttempt.current === attempt) onAuthenticated(issued);
    } catch (caught) {
      if (signInAttempt.current !== attempt) return;
      setError(
        caught instanceof IdentitySignInCancelledError
          ? "Sign-in was closed before it completed."
          : "Sign-in did not complete. Try again, or contact your administrator.",
      );
    } finally {
      if (signInAttempt.current === attempt) {
        window.clearTimeout(signInFeedbackTimeout.current);
        signInFeedbackTimeout.current = undefined;
        setBusy(false);
      }
    }
  }

  return (
    <main className="entry-screen">
      <section className="entry-pitch" aria-labelledby="entry-pitch-heading">
        <p className="eyebrow">POLYFUTURE · RISK INTELLIGENCE</p>
        <h1 id="entry-pitch-heading">Understand your investments. See what could change.</h1>
        <p className="entry-lede">
          For people investing for themselves and teams managing client portfolios. Ask questions,
          explore possible gains and losses, and track changes that matter to your holdings.
        </p>
        <ol className="entry-promises">
          {PROMISES.map((promise) => (
            <li key={promise.number}>
              <span className="entry-promise-number" aria-hidden="true">
                {promise.number}
              </span>
              <h2>{promise.title}</h2>
              <p>{promise.body}</p>
            </li>
          ))}
        </ol>
        <p className="entry-disclosure">ANALYSIS ONLY · NOT INVESTMENT ADVICE</p>
      </section>

      <section className="entry-panel" aria-labelledby="entry-create-heading">
        <p className="eyebrow">CREATE AN ACCOUNT</p>
        <h2 id="entry-create-heading">Get started with Polyfuture</h2>
        <p>
          Sign in, choose how you invest, and add your holdings. Polyfuture analyses the information
          you share and never places an order.
        </p>

        {config.restrictedAccess && error === undefined && authenticationError === undefined ? (
          <p className="entry-notice" role="status">
            Access is allowed only for approved accounts.
          </p>
        ) : null}

        {config.selfServeOrgCreation ? null : (
          <p className="entry-notice" role="status">
            Self-serve organization creation is disabled in this environment. Ask an administrator
            to invite you, then sign in.
          </p>
        )}

        {error === undefined && authenticationError === undefined ? null : (
          <p className="entry-error" role="alert">
            {error ?? authenticationError}
          </p>
        )}

        <button
          className="button button-primary entry-provider"
          type="button"
          onClick={() => void continueWithGoogle()}
          disabled={!googleAvailable || busy || !config.selfServeOrgCreation}
        >
          {busy ? "Opening Google sign-in…" : "Continue with Google"}
        </button>
        {googleAvailable ? null : (
          <p className="entry-provider-reason">
            Google sign-in is unavailable because Identity Platform is not configured here.
          </p>
        )}

        <details className="advanced-options">
          <summary>Other sign-in options</summary>
          <ul className="entry-unavailable">
            {UNAVAILABLE_PROVIDERS.map((provider) => (
              <li key={provider.label}>
                <span className="entry-unavailable-label">{provider.label}</span>
                <span className="entry-unavailable-state">Not available</span>
                <span className="entry-unavailable-reason">{provider.reason}</span>
              </li>
            ))}
          </ul>
        </details>

        <p className="entry-footnote">
          Already have an organization?{" "}
          <button
            className="link-button"
            type="button"
            onClick={() => void continueWithGoogle()}
            disabled={!googleAvailable || busy}
          >
            {busy ? "Opening Google sign-in…" : "Sign in"}
          </button>
        </p>
      </section>
    </main>
  );
}
